import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import { telemetry } from "globals/endpoints";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  TextField,
  IconButton,
  Card,
  Tooltip,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  AppBar,
  Toolbar,
  Icon,
  Badge,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MDTypography from "components/MDTypography";
import { makeStyles } from "@material-ui/core";
import { useMaterialUIController } from "context";
import GroupIcon from "@mui/icons-material/Group";
import EmptyImage from "../../../../../src/assets/images/mediacenter/emptyFolder.svg";
import CTLNotification from "application/components/CTLNotification";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideocamIcon from "@mui/icons-material/Videocam";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import VideoCallRoundedIcon from "@mui/icons-material/VideoCallRounded";
import PlayerWithAuth from "./PlayerWithAuth";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import { CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { setClickToCall } from "context";
import UserAndGroupList from "./userGroupList";
import { FixedSizeList as VirtualizedList } from "react-window";
import { setRTCEvent } from "context";
import { setClickToVideoCall } from "context";
import theme from "assets/theme";

const DISPATCHER_OPEN_CHAT_EVENT = "dispatcher-open-chat";

const ChatListOuter = React.forwardRef(function ChatListOuter(props, ref) {
  return (
    <div
      ref={ref}
      {...props}
      className={`chat-no-scrollbar ${props.className || ""}`}
      style={{
        ...props.style,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {props.children}
    </div>
  );
});

export default function ChatDispatcher({ contacts = [], contactsLoading = false, onRefreshContacts }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode, dispatcherEvent } = controller;
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerUrl, setPlayerUrl] = useState("");
  const [playerTitle, setPlayerTitle] = useState("");
  const [playFileType, setPlayFileType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear currently opened chat when component unmounts
      window.currentOpenedChat = null;
      console.log("Chat component unmounted - cleared currentOpenedChat");
    };
  }, []);

  const openChatFromDispatcherTarget = useCallback((detail = {}) => {
    const target = String(detail.offEXtn || detail.extension || "").trim();

    if (!target) {
      return false;
    }

    setSelectedGroup("");
    setSelectedUser({
      ...detail,
      offEXtn: target,
      extension: target,
      designation: detail.designation || detail.name || target,
      name: detail.name || detail.designation || target,
    });

    return true;
  }, []);

  useEffect(() => {
    const handleOpenChatFromDispatcher = (event) => {
      if (openChatFromDispatcherTarget(event?.detail || {})) {
        window.pendingDispatcherChatTarget = null;
      }
    };

    if (
      window.pendingDispatcherChatTarget &&
      openChatFromDispatcherTarget(window.pendingDispatcherChatTarget)
    ) {
      window.pendingDispatcherChatTarget = null;
    }

    window.addEventListener(
      DISPATCHER_OPEN_CHAT_EVENT,
      handleOpenChatFromDispatcher,
    );

    return () => {
      window.removeEventListener(
        DISPATCHER_OPEN_CHAT_EVENT,
        handleOpenChatFromDispatcher,
      );
    };
  }, [openChatFromDispatcherTarget]);

  const useStyles = makeStyles((theme) => ({
    root: {
      height: "100%",
      overflow: "hidden",
      width: "100%",
      position: "relative",
      padding: "8px",
      display: "flex",
      flexDirection: "column",
      borderRadius: 16,
      background:
        theme.palette.mode === "dark"
          ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
          : `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.98)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
      border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
      boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.08)}`,
    },
    messageContainer: {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: "10px",
    },
    senderMessage: {
      alignSelf: "flex-end",
      textAlign: "left",
      maxWidth: "72%",
      maxHeight: 150,
      overflowY: "auto",
      overflowX: "hidden",
      borderRadius: 14,
      padding: "8px 10px",
      marginBottom: 4,
      marginRight: 4,
      color:
        theme.palette.mode === "dark"
          ? theme.palette.common.white
          : theme.palette.common.white,
      background: `linear-gradient(135deg, ${theme.palette.mode === "dark"
          ? alpha(theme.palette.info.main, 0.58)
          : alpha(theme.palette.info.main, 0.62)
        } 0%, ${theme.palette.mode === "dark"
          ? alpha(theme.palette.info.dark, 0.58)
          : alpha(theme.palette.info.dark, 0.7)
        } 100%)`,
      border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
      boxShadow: `0 8px 18px ${alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.32 : 0.24)}`,
      wordBreak: "break-word",
      whiteSpace: "pre-wrap",
      "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
      },
    },

    receiverMessage: {
      alignSelf: "flex-start",
      textAlign: "left",
      maxWidth: "72%",
      maxHeight: 150,
      overflowY: "auto",
      overflowX: "hidden",
      borderRadius: 14,
      padding: "8px 10px",
      marginBottom: 4,
      color: theme.palette.text.primary,
      background:
        theme.palette.mode === "dark"
          ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.28)} 0%, ${alpha(theme.palette.info.dark, 0.24)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.5)} 0%, ${alpha(theme.palette.info.main, 0.16)} 100%)`,
      border: `1px solid ${alpha(theme.palette.info.main, 0.24)}`,
      boxShadow: `0 6px 16px ${alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.22 : 0.12)}`,
      wordBreak: "break-word",
      whiteSpace: "pre-wrap",
      "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
      },
    },
  }));

  const classes = useStyles();

  useEffect(() => {
    if (selectedGroup?.extension) {
      chatHistory(selectedGroup.extension, "G");
      // Store currently opened group in window object
      window.currentOpenedChat = {
        type: 'group',
        id: selectedGroup.extension
      };
      console.log("Opened group chat:", selectedGroup.extension);
      // Clear notifications for this group using extension
      if (window.clearChatNotification) {
        window.clearChatNotification(selectedGroup.extension);
      }
    } else if (selectedUser?.offEXtn) {
      chatHistory(selectedUser.offEXtn, "D");
      // Store currently opened user in window object
      window.currentOpenedChat = {
        type: 'user',
        id: selectedUser.offEXtn
      };
      console.log("Opened user chat:", selectedUser.offEXtn);
      // Clear notifications for this user using offEXtn
      if (window.clearChatNotification) {
        window.clearChatNotification(selectedUser.offEXtn);
      }
    } else {
      // No chat selected, clear the opened chat
      window.currentOpenedChat = null;
    }
  }, [selectedGroup?.extension, selectedUser?.offEXtn]);

  useEffect(() => {
    if (dispatcherEvent.messageType === "broadcast-text-message") {
      let innerMessage = dispatcherEvent.message;

      if (typeof innerMessage === "string") {
        const receiverMatch = innerMessage.match(/receiver=(\d+)/);
        const contentMatch = innerMessage.match(
          /content=(.*?)(?=,\s*messageType=)/,
        );
        const senderMatch = innerMessage.match(/sender=(\d+)/);
        const createdAtMatch = innerMessage.match(/createdAt=([\d\-T:\.]+)/);
        const messageTypeMatch = innerMessage.match(/messageType=([^,]+)/);
        const referenceId = innerMessage.match(/referenceId=(\d+)/);

        innerMessage = {
          receiver: receiverMatch ? receiverMatch[1] : null,
          content: contentMatch ? contentMatch[1] : "",
          sender: senderMatch
            ? senderMatch[1]
            : dispatcherEvent.userName || "unknown",
          createdAt: createdAtMatch[1],
          messageType: messageTypeMatch ? messageTypeMatch[1] : "text",
          referenceId: referenceId[1],
        };
      }

      console.log("Chat message received:", {
        receiver: innerMessage.receiver,
        sender: innerMessage.sender,
        selectedGroup: selectedGroup?.extension,
        selectedUser: selectedUser?.offEXtn
      });

      // Check if message is for currently selected group OR user
      const isForSelectedGroup = selectedGroup?.extension &&
        innerMessage.receiver === selectedGroup.extension;
      const isForSelectedUser = selectedUser?.offEXtn &&
        String(innerMessage.sender) === String(selectedUser.offEXtn);

      if (isForSelectedGroup || isForSelectedUser) {
        console.log("Message is for selected chat, adding to messages");
        setMessages((prev) => [...prev, innerMessage]);
      }
    }
  }, [dispatcherEvent, selectedGroup?.extension, selectedUser?.offEXtn]);

  const chatHistory = async (groupExtension, kind) => {
    if (!groupExtension) return;
    setMessages([]);
    setHistoryLoading(true);
    try {
      const payload = {
        kind: kind,
        sender: AuthenticationService.getExtensionNumber(),
        target: groupExtension,
        uptoSequence: 0,
      };
      const response = await telemetry.post("chat/messages", payload);
      setMessages(response?.data?.data || []);
    } catch (err) {
      setMessages([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    const payload = {
      kind: selectedGroup ? "G" : "D",
      sender: AuthenticationService.getExtensionNumber(),
      target: selectedGroup?.extension
        ? selectedGroup.extension
        : selectedUser.offEXtn,
      content: newMessage,
      messageType: "text",
    };
    try {
      await telemetry.post("/chat/message", payload);
      setNewMessage("");
      if (selectedUser) {
        const now = new Date();
        const newMsg = {
          id: Math.floor(Math.random() * 1000000),
          conversationId: selectedGroup
            ? selectedGroup.id || 0
            : selectedUser?.id || 0,
          sequence: 0,
          sender: payload.sender,
          receiver: payload.target,
          createdAt: [
            now.getFullYear(),
            now.getMonth() + 1,
            now.getDate(),
            now.getHours(),
            now.getMinutes(),
            now.getSeconds(),
            now.getMilliseconds() * 1000000,
          ],
          content: newMessage,
          messageType: "text",
          referenceId: 0,
          referenceUUID: "",
        };
        setMessages((prev) => [...prev, newMsg]);
      }
    } catch (err) {
      setNotify({
        isOpen: true,
        pagename: "Chat",
        status: "Failed to send message",
        type: "error",
      });
    }
  };

  const uploadFile = async (
    file,
    {
      fileDescription = "",
      groupId = "",
      userId = null,
      groupUpload = true,
    } = {},
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileDescription", fileDescription);
      formData.append("groupId", groupId);
      formData.append("userId", userId);
      formData.append("groupUpload", groupUpload ? true : false);

      const response = await telemetry.post("fileUpload/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotify({
        isOpen: true,
        pagename: "Chat",
        status: response.data.message,
        type: "success",
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        pagename: "Chat",
        status: "File upload failed",
        type: "error",
      });
      throw error;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    console.log(
      "Uploading file to group",
      selectedGroup?.extension ? selectedGroup.extension : selectedUser.offEXtn,
    );
    try {
      await uploadFile(file, {
        fileDescription: file?.name,
        groupId: selectedGroup?.departmentDescription,
        userId: selectedUser?.name ? selectedUser?.name : selectedUser?.designation,
        groupUpload: selectedGroup?.departmentDescription ? true : false,
      });
      if (selectedUser) {
        chatHistory(selectedUser.offEXtn, "D");
      }
    } catch (err) {
      setNotify({
        isOpen: true,
        pagename: "Chat",
        status: "Failed to send file message",
        type: "error",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const formatTime = (createdAt) => {
    if (!createdAt) return "";
    let date;
    if (Array.isArray(createdAt)) {
      const [year, month, day, hour, minute, second] = createdAt;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (createdAt instanceof Date) {
      date = createdAt;
    } else {
      date = new Date(createdAt);
    }

    if (isNaN(date.getTime())) return "";

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateHeader = (createdAt) => {
    let date;
    if (Array.isArray(createdAt)) {
      const [year, month, day, hour, minute, second] = createdAt;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (createdAt instanceof Date) {
      date = createdAt;
    } else {
      date = new Date(createdAt);
    }
    if (isNaN(date.getTime())) return "";

    const today = new Date();
    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return "Today";
    }

    return date.toLocaleDateString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const playFile = (msg) => {
    try {
      const url = `fileUpload/play1/${msg.referenceId}`;
      const onlyFileName = msg.content?.split("/").pop() || msg.content;
      if (msg.messageType === "audio" || msg.messageType === "video") {
        setPlayerUrl(url);
        setPlayFileType(msg.messageType);
        setPlayerTitle(onlyFileName);
        setPlayerOpen(true);
      } else if (msg.messageType === "image") {
        setPlayerUrl(url);
        setPlayFileType(msg.messageType);
        setPlayerTitle(onlyFileName);
        setPlayerOpen(true);
      } else {
        setNotify({
          isOpen: true,
          pagename: "Chat",
          status: "Preview not supported",
          type: "info",
        });
      }
    } catch (err) {
      setNotify({
        isOpen: true,
        pagename: "Chat",
        status: "Failed to play file",
        type: "error",
      });
    }
  };

  const downloadFile = async (msg) => {
    try {
      const url = `fileUpload/play1/${msg.referenceId}`;
      const res = await telemetry.get(url, { responseType: "blob" });
      const blob = res.data;

      // file name
      const cd = res.headers["content-disposition"];
      let name = msg.content?.split("/").pop() || "file";
      if (cd) {
        const match = cd.match(/filename="?(.+)"?/);
        if (match) name = match[1];
      }

      const blobUrl = URL.createObjectURL(blob);
      // create a hidden link and click it
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const renderMessageContent = (msg) => {
    const fileName = msg.content?.split("/").pop();

    const FileLabel = ({ text }) => (
      <MDTypography
        variant="body2"
        noWrap
        sx={{
          maxWidth: 92,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          verticalAlign: "middle",
          fontSize: "0.64rem",
          ml: 0.25,
          fontWeight: 600,
        }}
      >
        {text}
      </MDTypography>
    );

    switch (msg.messageType) {
      case "audio":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, px: 0.45, py: 0.2, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12) }}>
            <IconButton onClick={() => playFile(msg)} color="warning" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
              <AudiotrackIcon sx={{ fontSize: 14 }} />
              <FileLabel text={fileName} />
            </IconButton>
            <Tooltip title="Download">
              <IconButton onClick={() => downloadFile(msg)} color="secondary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "video":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, px: 0.45, py: 0.2, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12) }}>
            <IconButton onClick={() => playFile(msg)} color="primary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
              <VideocamIcon sx={{ fontSize: 14 }} />
              <FileLabel text={fileName} />
            </IconButton>
            <Tooltip title="Download">
              <IconButton onClick={() => downloadFile(msg)} color="secondary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "image":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, px: 0.45, py: 0.2, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12) }}>
            <IconButton onClick={() => playFile(msg)} color="success" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
              <ImageIcon sx={{ fontSize: 14 }} />
              <FileLabel text={fileName} />
            </IconButton>
            <Tooltip title="Download">
              <IconButton onClick={() => downloadFile(msg)} color="secondary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "file":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, px: 0.45, py: 0.2, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12) }}>
            <IconButton onClick={() => playFile(msg)} color="info" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
              <InsertDriveFileIcon sx={{ fontSize: 14 }} />
              <FileLabel text={fileName} />
            </IconButton>

            <Tooltip title="Download">
              <IconButton onClick={() => downloadFile(msg)} color="secondary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );

      case "others":
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35, px: 0.45, py: 0.2, borderRadius: 1.5, backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12) }}>
            <IconButton onClick={() => playFile(msg)} color="error" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
              <InsertDriveFileIcon sx={{ fontSize: 14 }} />
              <FileLabel text={fileName} />
            </IconButton>

            <Tooltip title="Download">
              <IconButton onClick={() => downloadFile(msg)} color="secondary" size="small" sx={{ p: 0.25, borderRadius: 1 }}>
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return (
          <MDTypography variant="body2" sx={{ fontSize: "0.68rem", lineHeight: 1.35, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {msg.content}
          </MDTypography>
        );
    }
  };

  const clickToGroupCall = () => {
    setRTCEvent(dispatch, {
      value: selectedGroup.departmentDescription,
      type: "GroupCall",
      data: selectedGroup.extension,
    });
    console.log("Group call clicked for", selectedGroup.extension);
  };

  const clickToCall = () => {
    setClickToCall(dispatch, selectedUser.offEXtn);
    console.log("Group call clicked for", selectedUser.offEXtn);
  };

  const clickToVideoCall = () => {
    setClickToVideoCall(dispatch, selectedUser.offEXtn);
    console.log("Group call clicked for", selectedUser.offEXtn);
  };

  const handleBack = () => {
    setSelectedGroup("");
    setSelectedUser("");
    setMessages([]);
    // Clear currently opened chat when going back
    window.currentOpenedChat = null;
    console.log("Cleared currently opened chat");
  };

  const filteredMessages = useMemo(
    () =>
      messages.filter(
        (message) =>
          message.messageType !== "call" &&
          message.messageType !== "PlayPublishVideo" &&
          message.messageType !== "StopPublishVideo",
      ),
    [messages],
  );

  const listRef = useRef();

  useEffect(() => {
    if (listRef.current) {
      requestAnimationFrame(() => {
        const lastIndex = filteredMessages.length - 1;
        if (lastIndex >= 0) {
          listRef.current.scrollToItem(lastIndex, "smart");
        }
      });
    }
  }, [filteredMessages]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        height: "100%",
        color: theme.palette.text,
        gap: 1,
      })}
    >
      <UserAndGroupList
        useStyles={useStyles}
        setSelectUser={setSelectedUser}
        setSelectGroup={setSelectedGroup}
        selectGroup={selectedGroup}
        selectUser={selectedUser}
        contacts={contacts}
        contactsLoading={contactsLoading}
        onRefreshContacts={onRefreshContacts}
      />

      {selectedGroup || selectedUser ? (
        <>
          <Card className={classes.root}>
            <AppBar
              position="sticky"
              color={"info"}
              elevation={1}
              sx={(theme) => ({
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                color: theme.palette.info.contrastText,
                boxShadow: `0 8px 20px ${alpha(theme.palette.info.main, 0.25)}`,
              })}
            >
              <Toolbar
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  minHeight: "40px !important",
                  px: 0.5,
                  py: 0,
                }}
              >
                {/* LEFT SIDE */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                  <IconButton
                    onClick={handleBack}
                    edge="start"
                    color="inherit"
                    size="small"
                    sx={{
                      p: 0.55,
                      borderRadius: 1.5,
                      backgroundColor: (theme) => alpha(theme.palette.common.white, 0.14),
                    }}
                  >
                    <ArrowBackRoundedIcon sx={{ fontSize: 17 }} />
                  </IconButton>

                  {selectedGroup && (
                    <>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <GroupIcon sx={{ fontSize: 15 }} />
                      </Avatar>
                      <MDTypography
                        variant="body2"
                        noWrap
                        sx={{ fontSize: "0.78rem", maxWidth: 140 }}
                      >
                        {selectedGroup.departmentDescription ? selectedGroup.departmentDescription : selectedGroup.department}
                      </MDTypography>
                      <Chip
                        label={selectedGroup.extension}
                        size="small"
                        // variant="outlined"
                        sx={{
                          fontSize: "0.56rem",
                          borderRadius: 1,
                          "& .MuiChip-label": { px: 0.7 },
                        }}>

                      </Chip>
                    </>
                  )}
                  {selectedUser && (
                    <>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <PersonIcon sx={{ fontSize: 15 }} />
                      </Avatar>
                      <MDTypography
                        variant="body2"
                        noWrap
                        sx={{ fontSize: "0.78rem", maxWidth: 140 }}
                      >
                        {selectedUser?.designation
                          ? selectedUser.designation
                          : selectedUser.name}
                      </MDTypography>
                      <Chip
                      label= {selectedUser?.offEXtn}
                        size="small"
                        // variant="outlined"
                        sx={{
                          fontSize: "0.56rem",
                          borderRadius: 1,
                          "& .MuiChip-label": { px: 0.7 }
                        }}
                          >
                      
                      </Chip>
                    </>
                  )}
                </Box>

                {/* RIGHT SIDE (ALWAYS END) */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: "auto",
                    gap: 0.5,
                  }}
                >
                  {selectedGroup && (
                    <IconButton
                      onClick={clickToGroupCall}
                      edge="end"
                      color="inherit"
                      size="small"
                      sx={{
                        p: 0.55,
                        borderRadius: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.common.white, 0.14),
                      }}
                    >
                      <PhoneInTalkRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}

                  {selectedUser && (
                    <>
                      <IconButton
                        onClick={clickToCall}
                        edge="end"
                        color="inherit"
                        size="small"
                        sx={{
                          p: 0.55,
                          borderRadius: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.common.white, 0.14),
                        }}
                      >
                        <PhoneInTalkRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>

                      <IconButton
                        onClick={clickToVideoCall}
                        edge="end"
                        color="inherit"
                        size="small"
                        sx={{
                          p: 0.55,
                          borderRadius: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.common.white, 0.14),
                        }}
                      >
                        <VideoCallRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Toolbar>
            </AppBar>
            <Box
              sx={(theme) => ({
                flex: 1,
                overflow: "hidden",
                p: 0.35,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                "& .chat-no-scrollbar::-webkit-scrollbar": {
                  width: 0,
                  height: 0,
                  display: "none",
                },
              })}
            >
              {historyLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    backdropFilter: "blur(5px)",
                    backgroundColor: darkMode ? theme.palette.background.paper + "cc" : theme.palette.common.white + "cc",
                    zIndex: 2,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={32} color="info" />
                  <MDTypography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text", fontSize: "0.72rem" }}
                  >
                    Loading messages…
                  </MDTypography>
                </Box>
              )}
              {!historyLoading && filteredMessages.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src={EmptyImage}
                    alt="No messages"
                    style={{ width: 80, height: 80, opacity: 0.5 }}
                  />
                  <MDTypography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text", fontSize: "0.72rem" }}
                  >
                    No messages yet
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ fontSize: "0.62rem", color: "text", textAlign: "center", px: 2 }}
                  >
                    Send a message to start the conversation
                  </MDTypography>
                </Box>
              )}
              <VirtualizedList
                ref={listRef}
                height={Math.max(300, window.innerHeight - 200)}
                itemCount={filteredMessages.length}
                itemSize={112}
                width={"100%"}
                outerElementType={ChatListOuter}
              >
                {({ index, style }) => {
                  const msg = filteredMessages[index];
                  const isMe =
                    msg.sender == AuthenticationService.getExtensionNumber();

                  const currentDateHeader = formatDateHeader(msg.createdAt);
                  const prevMsg = index > 0 ? filteredMessages[index - 1] : null;
                  const prevDateHeader = prevMsg
                    ? formatDateHeader(prevMsg.createdAt)
                    : null;
                  const showDateDivider = currentDateHeader !== prevDateHeader;

                  return (
                    <div
                      style={{
                        ...style,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {showDateDivider && (
                        <MDTypography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "center",
                            my: 0.3,
                            fontSize: "0.56rem",
                            color: "text",
                          }}
                        >
                          {currentDateHeader}
                        </MDTypography>
                      )}

                      <Box
                        className={
                          isMe ? classes.senderMessage : classes.receiverMessage
                        }
                        sx={{ alignSelf: isMe ? "flex-end" : "flex-start" }}
                      >
                        {selectedGroup &&
                          <MDTypography
                            variant="body2"
                            sx={{ fontWeight: "bold", mb: 0.1, fontSize: "0.62rem", lineHeight: 1.1 }}
                          >
                            {isMe ? "You" : msg.sender}
                          </MDTypography>
                        }

                        {renderMessageContent(msg)}

                        <MDTypography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: isMe ? "right" : "left",
                            mt: 0.08,
                            fontSize: "0.55rem",
                            opacity: 0.7,
                            lineHeight: 1,
                          }}
                        >
                          {formatTime(msg.createdAt)}
                        </MDTypography>
                      </Box>
                    </div>
                  );
                }}
              </VirtualizedList>
            </Box>

            {(selectedGroup?.extension || selectedUser?.offEXtn) && (
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  p: 0.35,
                  borderRadius: "8px",
                  background: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type a message..."
                  variant="outlined"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  sx={{
                    input: (theme) => ({
                      color: theme.palette.text,
                      fontSize: "0.68rem",
                      padding: "6px 8px",
                    }),
                    borderRadius: "8px",
                    mr: 0.35,
                  }}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  InputProps={{
                    endAdornment: (
                      <>
                        <input
                          accept="*/*"
                          id="file-upload"
                          type="file"
                          style={{ display: "none" }}
                          onChange={handleFileUpload}
                        />
                        <label htmlFor="file-upload">
                          <Tooltip title="select file">
                            <IconButton
                              color="info"
                              component="span"
                              disabled={uploading}
                              size="small"
                              sx={{ p: 0.3 }}
                            >
                              {uploading ? (
                                <CircularProgress size={14} color="info" />
                              ) : (
                                <AttachFileIcon sx={{ fontSize: 14 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </label>
                      </>
                    ),
                  }}
                />
                <Tooltip title="Send Message">
                  <IconButton
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    size="small"
                    sx={{ p: 0.3 }}
                  >
                    <SendIcon
                      color={"info"}
                      sx={{ fontSize: 14 }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Card>
        </>
      ) : (
        <Card className={classes.root}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <img src={EmptyImage} alt="Empty Folder" style={{ height: "200px", width: "200px" }} />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <MDTypography variant="h6" >
                Select a Group to start chatting
              </MDTypography>
            </Box>
          </Box>
        </Card>
      )}

      <Dialog
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <DialogTitle
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            ...CTLDialogContent(theme, { darkMode }),
          })}
        >
          {playerTitle}
          <IconButton onClick={() => setPlayerOpen(false)}>
            <CloseIcon color="error" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <PlayerWithAuth playerUrl={playerUrl} playFileType={playFileType} />
        </DialogContent>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Box>
  );
}
